import React, {useCallback, useState} from 'react';
import {Typography} from '@mui/material';
import type {RowSelectionModel} from 'components/dataTable/types';
import {useLocalization} from 'localization';
import {ControlledIconButton, FlexBox, useSnacks} from 'components';
import {settlePromiseList} from '../functions/settlePromiseList';
import {useDeleteNorafUpdateIgnorelistItem} from '../hooks/useDeleteNorafUpdateIgnorelistItem';

type Props = {
  selection: RowSelectionModel;
  postProcessing: () => void;
  postSuccess: () => void;
};
export const NorafIgnoredListSelectionAction: React.FC<Props> = ({
  selection,
  postProcessing,
  postSuccess,
}) => {
  const {t} = useLocalization();
  const {successSnack, errorSnack} = useSnacks();

  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const deleter = useDeleteNorafUpdateIgnorelistItem();

  const handleDeleteSelected = useCallback(() => {
    setIsProcessing(true);

    const deletePromises: Promise<void>[] = [];
    selection.ids.forEach(id => {
      deletePromises.push(deleter(id as string));
    });

    const deletions = settlePromiseList(deletePromises);

    deletions.then(deleteResponse => {
      if (deleteResponse.rejected > 0) {
        errorSnack(
          t(`page.agents.noraf.ignores.delete.failed`, {
            count: deleteResponse.rejected,
          }),
        );
      } else {
        successSnack(
          t(`page.agents.noraf.ignores.delete.success`, {
            count: deleteResponse.fulfilled,
          }),
        );

        postSuccess();
      }

      postProcessing();
      setIsProcessing(false);
    });
  }, [
    deleter,
    errorSnack,
    postSuccess,
    postProcessing,
    selection,
    successSnack,
    t,
  ]);

  if (!selection.ids.size) {
    return null;
  }

  return (
    <FlexBox left center>
      <Typography variant="body1">
        {t(`page.agents.noraf.changes.selection`, {
          count: selection.ids.size,
        })}
      </Typography>
      <ControlledIconButton
        icon={'Delete'}
        postText={
          <Typography variant={'body1'}>
            {t('page.agents.noraf.ignores.delete.title')}
          </Typography>
        }
        onClick={handleDeleteSelected}
        isCalling={isProcessing}
        isDelayed={isProcessing}
        size={'small'}
      />
    </FlexBox>
  );
};
